import { useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';

export type Device = 'desktop' | 'tablet' | 'mobile';

/**
 * Device preview frame — v3 studio's morph (code.techedify.com/code-box/v3) with Sparked's
 * slide-editor zoom model.
 *
 * Two rules, and every past glitch came from breaking one of them:
 *
 * 1. ONE SCALE FACTOR. The device is a fixed 390×844 (or 820×1180) object; whatever the stage or
 *    the zoom does, both axes scale together and the bezel, radius, notch and home bar scale with
 *    them. Clamping each axis on its own squashed the phone into the wrong aspect ratio.
 * 2. ZOOM SCALES THE DEVICE, not the pixels inside it — true-scale, exactly like the Sparked slide
 *    editor: at 200% you get a bigger phone, still 390:844, and the workspace scrolls to reach the
 *    parts that no longer fit. (Zooming the content instead left the hardware the same size and
 *    reflowed the app, which is not what a device preview means.)
 *
 * Everything resolves to concrete pixels — including desktop, whose target is simply the measured
 * stage — because '100%' cannot interpolate against 390px, and that snap was the old
 * "the whole preview shifts before morphing" glitch on the desktop crossing.
 */
const SIZES: Record<Device, { w: number; h: number; radius: number; pad: number } | null> = {
  desktop: null,
  tablet: { w: 820, h: 1180, radius: 32, pad: 22 },
  mobile: { w: 390, h: 844, radius: 44, pad: 14 },
};

/* v3's frame-shell curve: fast start, tiny overshoot at the end so the resize lands with life */
const EASE = 'cubic-bezier(0.2, 0.9, 0.2, 1.1)';
const MORPH = ['width', 'height', 'border-radius', 'padding', 'background-color']
  .map(p => `${p} 0.32s ${EASE}`)
  .join(', ');

export function DeviceFrame({
  device,
  zoom = 1,
  scrollerRef,
  children,
}: {
  device: Device;
  zoom?: number;
  /** the workspace scroller, so the grab tool can pan a zoomed device from outside this component */
  scrollerRef?: RefObject<HTMLDivElement>;
  children: ReactNode;
}) {
  const stageRef = useRef<HTMLDivElement>(null);
  const [avail, setAvail] = useState<{ w: number; h: number } | null>(null);

  /* The measured element is the OUTER box, which never scrolls — measuring the scroller itself
     would shrink by its own scrollbar width the moment zoom overflowed it, and each shrink would
     feed back into the next scale. */
  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const measure = () => setAvail({ w: stage.clientWidth, h: stage.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(stage);
    return () => ro.disconnect();
  }, []);

  const spec = SIZES[device];
  const isDesktop = !spec;
  // fit at 100%, then zoom on top of it — one factor, applied to everything the device is made of
  const fit = spec && avail ? Math.min(1, avail.w / spec.w, avail.h / spec.h) : 1;
  const scale = fit * zoom;
  const w = spec ? Math.round(spec.w * scale) : Math.round((avail?.w ?? 0) * zoom);
  const h = spec ? Math.round(spec.h * scale) : Math.round((avail?.h ?? 0) * zoom);
  const pad = spec ? Math.max(Math.round(spec.pad * scale), 6) : 2;
  const radius = spec ? Math.max(Math.round(spec.radius * scale), 12) : 16;

  return (
    <div ref={stageRef} className="relative h-full w-full">
      {/* scroller: `m-auto` inside a min-full flex row centers the device when it fits AND still
          lets you scroll to its top-left when zoom makes it larger than the stage (plain
          justify/align centering clips the overflow unreachably) */}
      <div ref={scrollerRef} className="absolute inset-0 overflow-auto">
        <div className="flex min-h-full min-w-full">
          {avail && (
            <div
              className={['relative m-auto shrink-0 shadow-card', isDesktop ? 'bg-surface ring-1 ring-line' : 'bg-ink'].join(' ')}
              style={{ width: w, height: h, borderRadius: radius, padding: pad, transition: MORPH }}
            >
              {/* phone hardware stays mounted and fades, so mode changes morph instead of popping;
                  its dimensions ride the same scale factor as the bezel so a shrunken (or zoomed)
                  phone still looks like a phone, not one wearing an oversized notch */}
              <div
                className="pointer-events-none absolute left-1/2 z-10 -translate-x-1/2 rounded-b-2xl bg-ink transition-opacity duration-300"
                style={{ opacity: device === 'mobile' ? 1 : 0, top: pad, height: Math.round(28 * scale), width: Math.round(130 * scale) }}
              >
                <div className="absolute left-1/2 top-2 h-1 w-10 -translate-x-1/2 rounded-full bg-surface/25" />
              </div>
              <div
                className="pointer-events-none absolute left-1/2 z-10 h-1 -translate-x-1/2 rounded-full bg-surface/40 transition-opacity duration-300"
                style={{ opacity: device === 'mobile' ? 1 : 0, bottom: Math.max(Math.round(6 * scale), 3), width: Math.round(128 * scale) }}
              />
              <div
                className="pointer-events-none absolute left-1/2 top-[9px] h-2 w-2 -translate-x-1/2 rounded-full bg-surface/30 transition-opacity duration-300"
                style={{ opacity: device === 'tablet' ? 1 : 0 }}
              />
              <div
                className="h-full w-full overflow-hidden bg-white"
                style={{ borderRadius: Math.max(radius - pad, 4), transition: MORPH }}
              >
                {children}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Hardware dimensions — the device's REAL size, which zoom does not change. Pinned to the
          stage corner, not under the device: a zoomed device overflows the stage, and a centered
          badge then sits on top of the app it is describing. */}
      <span
        className="pointer-events-none absolute bottom-1 left-1 rounded-full bg-surface/90 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-muted shadow-sm backdrop-blur-sm transition-opacity duration-300"
        style={{ opacity: isDesktop ? 0 : 1 }}
      >
        {spec ? `${spec.w} × ${spec.h}` : ''}
      </span>
    </div>
  );
}
